'use client';

import { useRouter } from 'next/navigation';
import { useContext, useEffect, useMemo, useRef } from 'react';
import SortOption from '~/constants/common/SortOption';
import {
  paramsToRemove,
  utmContentToRemove
} from '~/constants/srp/paramsToRemove';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import { useDealershipId } from '~/context/dealership/StaticContext';
import InventoryContext from '~/context/srp/InventoryContext';
import SearchContext from '~/context/srp/SearchContext';
import {
  useBeginServerNavigation,
  useIsServerDriven
} from '~/context/srp/SrpNavModeContext';
import { stringifyAndEncode, stripEquals } from '~/utilities/base64';
import isServerSideRendering from '~/utilities/isServerSideRendering';
import { isReloadingWindow } from '~/utilities/reloadWindow';
import scrollToTop from '~/utilities/scrollToTop';

const emptyObjects = [
  stripEquals(stringifyAndEncode({})),
  stripEquals(stringifyAndEncode({ filters: {} }))
];

function useRemoveQueryParams(urlIn: URL) {
  return useMemo(() => {
    const urlOut = new URL(urlIn.toString());

    paramsToRemove.forEach(param => {
      urlOut.searchParams.delete(param);
    });

    utmContentToRemove.forEach(v => {
      if (urlOut.searchParams.get('utm_content') === v) {
        urlOut.searchParams.delete('utm_content');
      }
    });

    return urlOut;
  }, [urlIn]);
}

function useRemoveResolvedTsParam(urlIn: URL) {
  const { resolvedTextSearchFilters } = useContext(AppliedFiltersContext);

  return useMemo(() => {
    if (!resolvedTextSearchFilters) return urlIn;

    const urlOut = new URL(urlIn.toString());
    urlOut.searchParams.delete('ts');

    return urlOut;
  }, [resolvedTextSearchFilters, urlIn]);
}

function useSetFreeTextSearchParam(urlIn: URL) {
  const { freeTextNoResults, freeTextSearchQuery } =
    useContext(InventoryContext);

  return useMemo(() => {
    if (!freeTextNoResults) return urlIn;

    const urlOut = new URL(urlIn.toString());
    urlOut.searchParams.set('ts', freeTextSearchQuery);

    return urlOut;
  }, [freeTextNoResults, freeTextSearchQuery, urlIn]);
}

function useSetCvnaId(urlIn: URL) {
  const context = useContext(AppliedFiltersContext);
  const dealershipId = useDealershipId();
  const sortBy =
    context.sortBy === SortOption.MostPopular ? undefined : context.sortBy;

  return useMemo(() => {
    if (!context.filtersOrSortDidChange) return urlIn; // Filters & sort by have not changed, do not touch the cvnaid

    const useDealershipPathname =
      dealershipId && !isServerSideRendering
        ? window.location.pathname.includes('/dealership/')
        : false;

    const urlOut = new URL(urlIn.toString());

    // cvnaid is now the authoritative sort source, so a leftover plain
    // sortBy param (e.g. from a shared link) must not linger and win on a
    // future load via the query-param-beats-cvnaid precedence rule.
    urlOut.searchParams.delete('sortBy');

    const cvnaId = stripEquals(
      stringifyAndEncode({ filters: context.appliedFilters, sortBy })
    );

    if (emptyObjects.includes(cvnaId)) {
      urlOut.pathname = useDealershipPathname
        ? `/dealership/${dealershipId}/cars`
        : '/cars';
      urlOut.searchParams.delete('cvnaid');
    } else {
      urlOut.pathname = useDealershipPathname
        ? `/dealership/${dealershipId}/cars/filters`
        : '/cars/filters';
      urlOut.searchParams.set('cvnaid', cvnaId);
      // Real filters now take over as the authoritative source, so a
      // leftover free-text ts (from an earlier no-results resolve) must not
      // linger and win the query-param-beats-cvnaid precedence rule on
      // refresh. A ts belonging to an *empty* resolve is left alone here —
      // that's the freeTextNoResults state useSetFreeTextSearchParam owns.
      urlOut.searchParams.delete('ts');
    }

    return urlOut;
  }, [
    context.appliedFilters,
    context.filtersOrSortDidChange,
    dealershipId,
    sortBy,
    urlIn
  ]);
}

function useSetPageNumber(urlIn: URL) {
  const { pageNumber } = useContext(AppliedFiltersContext);

  return useMemo(() => {
    const urlOut = new URL(urlIn.toString());

    if (pageNumber <= 1) {
      urlOut.searchParams.delete('page');
    } else {
      urlOut.searchParams.set('page', String(pageNumber));
    }

    return urlOut;
  }, [pageNumber, urlIn]);
}

export default function useSyncUrl() {
  const { showFinance } = useContext(SearchContext);
  // turbo-boost experiment: server-driven treatment only
  const isServerDriven = useIsServerDriven();
  const beginServerNavigation = useBeginServerNavigation();
  const router = useRouter();
  // end turbo-boost experiment
  const isInitializedRef = useRef(false);
  const isUpdatingRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout>(null);

  const url = useSetCvnaId(
    useSetPageNumber(
      useSetFreeTextSearchParam(
        useRemoveResolvedTsParam(
          useRemoveQueryParams(
            new URL(
              isServerSideRendering
                ? 'https://www.dummy.com'
                : window.location.href
            )
          )
        )
      )
    )
  );
  // convert to string to prevent multiple re-renders in useEffect
  const urlString = useMemo(() => url.toString(), [url]);

  useEffect(() => {
    // A vanity-path popstate triggers a full reload; don't re-push a URL derived
    // from the not-yet-reset context state in the window before it commits.
    if (isReloadingWindow()) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const scheduleUnlock = () => {
      // Reset the lock after React has processed context updates
      timeoutRef.current = setTimeout(() => {
        isUpdatingRef.current = false;
      }, 100);
    };

    // Skip if we're in the middle of an update. Reschedule the unlock timer
    // we just cleared above so a change that arrives during the lock doesn't
    // strand it — otherwise isUpdatingRef never releases and every later
    // filter/sort change is silently dropped.
    if (isUpdatingRef.current) {
      scheduleUnlock();

      return;
    }

    if (window.location.href === urlString) {
      isInitializedRef.current = true;
    } else {
      isUpdatingRef.current = true;

      scrollToTop();

      if (isServerDriven) {
        // Skip the first sync so we don't navigate on initial load.
        if (isInitializedRef.current) {
          const target = new URL(urlString);
          // Flag a real navigation so useGetInventory shows the results skeleton.
          beginServerNavigation();
          // scroll: false — only the island page segment changes, so router's
          // default scroll would jump down to it; scrollToTop() above is intended.
          router.push(`${target.pathname}${target.search}`, { scroll: false });
        } else {
          isInitializedRef.current = true;
        }
      } else {
        // Embed SRP state in the history entry so back/forward navigation
        // can restore filters, page, and sort without sessionStorage.
        const historyState = { ...window.history.state, srp: urlString };

        if (!isInitializedRef.current) {
          window.history.replaceState(historyState, '', urlString);
          isInitializedRef.current = true;
        } else {
          window.history.pushState(historyState, '', urlString);
        }
      }

      scheduleUnlock();
    }
  }, [beginServerNavigation, isServerDriven, router, showFinance, urlString]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      isUpdatingRef.current = false;
    };
  }, []);
}
